import { partial } from 'lodash';
import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import Modal from '~/App/Components/Modal';
import { TRANSITION_TIME_MS } from '~/App/Components/Modal/Constants';
import ReplaceableComponents from '~/components/higher-order/ReplaceableComponents';
import { SubHeading } from '~/App/Components/Text';
import { StyleableProps } from '../HOC/Styleable';
import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createRoot } from 'react-dom/client';

export const components = {
  ContentContainer: SubHeading,
};

const confirmStyles: StyleableProps = {
  content: ({ base }) => ({
    ...base,
    padding: 0,
    marginTop: '20%',
    maxWidth: '520px',
  }),
  panel: ({ base }) => ({
    ...base,
  }),
};

const Confirm = ReplaceableComponents(components)(
  ({
    children,
    onConfirmClick,
    onClose,
    components,
    cancelButtonText,
    confirmButtonText,
  }) => {
    const { ContentContainer } = components;
    const [isOpen, setOpen] = useState(true);
    const onRequestClose = partial(setOpen, false);

    useEffect(() => {
      if (!isOpen && typeof onClose === 'function') onClose();
    }, [isOpen]);

    function handleConfirmClick() {
      onConfirmClick();
      onRequestClose();
    }

    const modalProps = {
      isOpen,
      onRequestClose,
      onConfirmClick: handleConfirmClick,
      cancelButtonText,
      confirmButtonText,
      styles: confirmStyles,
    };

    return (
      <Modal {...modalProps}>
        <div className='flex items-baseline justify-between'>
          <ContentContainer className='justify-between font-bold text-[20px] mt-0 mb-6 mx-0 text-neutral-700'>
            {children}
          </ContentContainer>
          <div className='p-0 m-0 b-0 bg-none'>
            <FontAwesomeIcon
              className='text-[25px] text-neutral-300 cursor-pointer'
              icon={faTimes}
              onClick={onRequestClose}
            />
          </div>
        </div>
      </Modal>
    );
  }
);

Confirm.propTypes = {
  children: PropTypes.any,
  onClose: PropTypes.func,
  components: PropTypes.object,
  onConfirmClick: PropTypes.func.isRequired,
};

export function confirmAsync(
  text: React.ReactNode,
  confirmButtonText: React.ReactNode = 'Ok',
  cancelButtonText: React.ReactNode = 'Cancel'
) {
  const renderTarget = document.createElement('div');
  document.body.appendChild(renderTarget);
  const root = createRoot(renderTarget);

  return new Promise<boolean>((resolve) => {
    function onClose() {
      setTimeout(() => {
        root.unmount();
        document.body.removeChild(renderTarget);
      }, TRANSITION_TIME_MS);
      resolve(false);
    }

    root.render(
      <Confirm
        onConfirmClick={() => resolve(true)}
        onClose={onClose}
        confirmButtonText={confirmButtonText}
        cancelButtonText={cancelButtonText}
      >
        {text}
      </Confirm>
    );
  });
}
